import { ROOT_HOSTNAMES } from "@/utils/appMode";

// The auth session lives in localStorage, which is scoped to a single origin.
// In subdomain mode (admin.<root> / app.<root> / {slug}.<root>) each subdomain
// is a separate origin with its own empty store, so hopping between panels
// would otherwise drop the user back on the login screen.
//
// This module hands the session off between our own subdomains: on the way out
// it attaches the tokens to the URL *fragment* (#), and on the way in it moves
// them into the target's localStorage and strips the fragment immediately.
//
// Why the fragment and not a query string: the fragment is never sent to the
// server, never appears in access logs, and never leaks via the Referer header.
// Combined with the immediate history.replaceState() strip below, the token
// never lingers where the query string would. This does not widen the existing
// attack surface — the same tokens already sit in localStorage — it only moves
// them, over HTTPS, between two origins we control.

const HANDOFF_PARAM = "session";
const ACCESS_KEY = "access_token";
const REFRESH_KEY = "refresh_token";

let _consumed = false;

// Swap only the first hostname segment (e.g. "admin" → "app"), preserving the
// rest of the host and the port (so localhost:3000 dev also works).
function swapLeadingSubdomain(targetSubdomain) {
  const segments = window.location.hostname.split(".");
  segments[0] = targetSubdomain;
  return segments.join(".");
}

// Guard: only ever attach tokens to a host we actually own — either a known
// root hostname, or a direct subdomain of one. Prevents a bug (or tampered
// input) from ever redirecting a session to an unrelated host.
function isOwnHostname(hostname) {
  if (ROOT_HOSTNAMES.includes(hostname)) return true;
  const parent = hostname.split(".").slice(1).join(".");
  return ROOT_HOSTNAMES.includes(parent);
}

/**
 * Full-page redirect to a sibling subdomain, carrying the current session so
 * the destination does not force a re-login. Falls back to a plain redirect
 * (no session) when there is no session to carry or the target is not ours.
 *
 * @param {string} targetSubdomain e.g. "admin" or "app"
 */
export function redirectToSubdomainWithSession(targetSubdomain) {
  const newHost = swapLeadingSubdomain(targetSubdomain);
  const port = window.location.port ? `:${window.location.port}` : "";
  const base = `${window.location.protocol}//${newHost}${port}/`;

  const access = localStorage.getItem(ACCESS_KEY);
  if (!access || !isOwnHostname(newHost)) {
    window.location.href = base;
    return;
  }

  const payload = { a: access };
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (refresh) payload.r = refresh;

  // encodeURIComponent first keeps btoa safe for any non-latin1 bytes.
  const encoded = btoa(encodeURIComponent(JSON.stringify(payload)));
  window.location.href = `${base}#${HANDOFF_PARAM}=${encoded}`;
}

/**
 * If the current URL carries a session handoff in its fragment, move the tokens
 * into localStorage and strip the fragment from the URL. Idempotent, and safe
 * (and intended) to call once at app startup, before any auth bootstrap reads
 * the token.
 *
 * @returns {boolean} true if a session was consumed
 */
export function consumeSessionHandoff() {
  if (_consumed) return false;
  _consumed = true;
  if (typeof window === "undefined") return false;

  const hash = window.location.hash || "";
  const marker = `${HANDOFF_PARAM}=`;
  const idx = hash.indexOf(marker);
  if (idx === -1) return false;

  const encoded = hash.slice(idx + marker.length).split("&")[0];

  // Always strip the fragment first, even if decoding fails below, so a token
  // never lingers in the address bar or browser history.
  const beforeMarker = hash.slice(0, idx).replace(/[#&]$/, "");
  const cleanUrl =
    window.location.pathname + window.location.search + beforeMarker;
  window.history.replaceState(null, "", cleanUrl);

  let payload = null;
  try {
    payload = JSON.parse(decodeURIComponent(atob(encoded)));
  } catch {
    payload = null;
  }

  if (payload && payload.a) {
    localStorage.setItem(ACCESS_KEY, payload.a);
    if (payload.r) localStorage.setItem(REFRESH_KEY, payload.r);
    return true;
  }
  return false;
}
